import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from "@/lib/utils"; // Assuming cn is globally available or imported from lib
import { Badge } from "@/components/ui/badge";
import {
  Wifi,        // For Device Uptime / Signal
  Truck,       // For Vehicle Status
  AlertTriangle, // For Active Alerts
  Database,    // For Data Processed
  Activity     // For Live Indicator
} from 'lucide-react';

// Animation variants (simplified for consistency)
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const metricCardAnimation = {
  hidden: { opacity: 0, scale: 0.95, y: 10 },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" }
  }
};

const dataPointAnimation = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2, ease: "easeIn" } }
};

// Mock data for the live metrics
const generateRandomData = () => ({
  deviceUptime: (99.5 + Math.random() * 0.4).toFixed(1), // Slightly less variation
  vehiclesOnline: 240 + Math.floor(Math.random() * 10), // More stable base
  activeAlerts: Math.floor(Math.random() * 3), // Fewer max alerts for cleaner UI
  edgeDataProcessed: (1.0 + Math.random() * 0.3).toFixed(1)
});

export default function LiveDataShowcase({ viewport, isRTL }) {
  const { t } = useTranslation();
  const { isMobile = false } = viewport || {}; // Only need isMobile for responsive variant decisions

  const [liveData, setLiveData] = useState(generateRandomData());
  
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveData(generateRandomData());
    }, 3000); // Update interval slightly faster for demo purposes, can be adjusted
    return () => clearInterval(interval);
  }, []);

  const metricsConfig = [
    {
      key: 'uptime',
      icon: Wifi,
      color: 'hsl(var(--green-500))',
      value: `${liveData.deviceUptime}%`
    },
    {
      key: 'vehicles',
      icon: Truck,
      color: 'hsl(var(--blue-500))',
      value: `${liveData.vehiclesOnline}/250`
    },
    {
      key: 'alerts',
      icon: AlertTriangle,
      color: liveData.activeAlerts > 1 ? 'hsl(var(--orange-500))' : 'hsl(var(--yellow-400))', // Softer alert colors
      value: liveData.activeAlerts
    },
    {
      key: 'processed',
      icon: Database,
      color: 'hsl(var(--sky-500))',
      value: `${liveData.edgeDataProcessed} M/s` // Changed unit for clarity
    }
  ];

  const metrics = metricsConfig.map(metric => ({
    ...metric,
    title: t(`homepage.liveData.metrics.${metric.key}`),
  }));

  return (
    <section 
      id="live-data" 
      className="relative py-20 md:py-28 lg:py-32 bg-gradient-to-b from-background to-muted/20" // Subtle gradient
    >
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerChildren}
          className="text-center mb-12 md:mb-20 max-w-2xl mx-auto"
        >
          <motion.div variants={fadeInUp}>
            <Badge
              variant="outline"
              className="mb-3 px-3 py-1 text-xs font-medium rounded-full border-primary/30 bg-primary/10 text-primary inline-flex items-center"
            >
              <Activity size={14} className="mr-1.5 animate-pulse" style={{ color: 'hsl(var(--green-500))' }}/>
              {t('homepage.liveData.badge', 'Streaming Live')}
            </Badge>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground">
              {t('homepage.liveData.title', 'Real-Time Operational Insights')}
            </h2>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <p className="text-md md:text-lg text-muted-foreground max-w-xl mx-auto">
              {t('homepage.liveData.subtitle', 'Witness the flow of information as it happens. Our platform provides instant visibility into your critical assets and operations.')}
            </p>
          </motion.div>
        </motion.div>
        
        <motion.div
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1 }}
          className="grid grid-cols-4 sm:grid-cols-1 md:grid-cols-2 gap-5 md:gap-6" // Slightly reduced gap
        >
          {metrics.map((metric) => {
            const IconComponent = metric.icon;
            return (
              <motion.div
                key={metric.key}
                variants={metricCardAnimation} // Use the specific animation for cards
                className="flex" // Ensure cards in a row take same height if content differs
              >
                <div className={cn(
                  "w-full bg-background/70 backdrop-blur-md rounded-xl p-5 md:p-6",
                  "border border-border/20 shadow-lg shadow-primary/5",
                  "transition-all duration-300 ease-in-out",
                  "hover:border-primary/30 hover:shadow-primary/10 hover:-translate-y-1",
                  "flex flex-col group" // Group for icon hover, flex-col for structure
                )}>
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">
                      {metric.title}
                    </h3>
                    <div 
                      className="w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center transition-colors duration-300 group-hover:bg-opacity-20"
                      style={{ 
                        backgroundColor: `color-mix(in srgb, ${metric.color} 10%, transparent)`,
                        color: metric.color 
                      }}
                    >
                      <IconComponent size={isMobile ? 18 : 20} />
                    </div>
                  </div>
                  
                  <div className="mt-auto"> {/* Pushes value to bottom if card height varies and has extra space */}
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={metric.value} // Keyed for re-animation on change
                        variants={dataPointAnimation}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        className={cn(
                          "text-2xl md:text-3xl font-bold tracking-tight",
                          "bg-clip-text text-transparent"
                        )}
                        style={{ backgroundImage: `linear-gradient(to right, ${metric.color}, color-mix(in srgb, ${metric.color} 60%, hsl(var(--foreground))))`}}
                      >
                        {metric.value}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.p 
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          className="text-center mt-10 text-xs text-muted-foreground/80"
        >
          {t('homepage.liveData.updateNotice', 'Data updates every 3 seconds for demonstration purposes.')}
        </motion.p>

      </div>
    </section>
  );
} 